import React from 'react';
import { useInstance } from '../support/ioc/useInstance';
import { State, useSubscription } from '@autopylot-internal/state';
import { Page } from '../components/page/Page.tsx';
import { OperatorState } from '../profile/OperatorState.ts';
import { TrackingService } from '../support/tracking/TrackingService.ts';
import { AuthService } from '../auth/AuthService.ts';
import { Welcome } from './Welcome.tsx';
import { Terms } from './Terms.tsx';
import { Onboarding } from './Onboarding.tsx';
import { SetupOperator } from './SetupOperator.tsx';

export class SetupPageState extends State<{started: boolean}> {
    constructor() {
        super({ started: false });
    }
}

type ISetupPage = 'Welcome' | 'Terms' | 'Onboarding' | 'Profile';

export const SetupPage: React.FC = () => {
    const tracker = useInstance(TrackingService);

    const operatorState = useInstance(OperatorState);
    useSubscription(() => operatorState);

    const pageState = useInstance(SetupPageState);
    useSubscription(() => pageState);

    const authService = useInstance(AuthService);

    const acceptTerms = async () => {
        tracker.track('Terms Accepted', () => ({ autoAccepted: true }));
        await operatorState.save({ agreedToTermsOfService: true, agreedToFAAPrivacyStatement: true, agreedToLAANCStatement: true });
    };

    const getStarted = () => {
        pageState.setState({ started: true });
    };

    const restart = () => {
        pageState.setState({ started: false });
    };

    const doneOnboarding = () => {
        operatorState.setOnboardingShown(true);
    };

    const signIn = async (fromPage: 'Welcome' | 'Terms') => {
        tracker.track(`${fromPage} | Sign In`);
        await authService.showLoginDialog({ message: 'Sign up for free, no password required!' });
    };

    const isGuest = operatorState.isGuest();
    const page: ISetupPage = isGuest && !pageState.state.started ? 'Welcome' : operatorState.needsOnboarding() ? 'Onboarding' : operatorState.needsConsent() ? 'Terms' : 'Profile';
    const fullWidth = (page === 'Onboarding');

    return (
        <Page fullWidth={fullWidth} noScroll hideMenu>
            {page === 'Welcome' &&
                <Welcome getStarted={getStarted} signIn={signIn} />
            }

            {page === 'Terms' &&
                <Terms acceptTerms={acceptTerms} />
            }

            {page === 'Onboarding' &&
                <Onboarding onDone={doneOnboarding} onCancel={isGuest ? restart : undefined} />
            }

            {page === 'Profile' &&
                <SetupOperator />
            }
        </Page>
    );
};
